import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { ArrowLeft, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import AuditLogFilterPanel from '../../components/warehouse/AuditLogFilterPanel';
import AuditLogTable from '../../components/warehouse/AuditLogTable';
import { Pagination } from '../../components/UI/Pagination';

export const AuditLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters from URL params
  const [filters, setFilters] = useState({
    warehouseId: searchParams.get('warehouseId')
      ? parseInt(searchParams.get('warehouseId')!, 10)
      : undefined,
    actionType: searchParams.get('actionType') || undefined,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
  });

  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get('pageSize') || '50', 10)
  );

  useEffect(() => {
    loadAuditLogs();
  }, [filters, page, pageSize]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.warehouseId) params.set('warehouseId', filters.warehouseId.toString());
    if (filters.actionType) params.set('actionType', filters.actionType);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (page > 1) params.set('page', page.toString());
    if (pageSize !== 50) params.set('pageSize', pageSize.toString());
    setSearchParams(params);
  }, [filters, page, pageSize]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const result = await warehouseAPI.getAuditLogs({
        ...filters,
        page,
        pageSize,
      });
      setLogs(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      toast.error(t('warehouse.auditLog.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  };

  const handleResetFilters = () => {
    setFilters({
      warehouseId: undefined,
      actionType: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await warehouseAPI.downloadAuditLogExport(filters);
      toast.success(t('warehouse.auditLog.exportSuccess'));
    } catch (error) {
      toast.error(t('warehouse.auditLog.exportError'));
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{t('warehouse.auditLog.title')}</h1>
            <p className="text-gray-500 text-sm">{t('warehouse.auditLog.description')}</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          {t('warehouse.auditLog.exportLogs')}
        </button>
      </div>

      {/* Filters */}
      <AuditLogFilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
      />

      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow">
        <AuditLogTable logs={logs} loading={loading} />

        {totalPages > 1 && (
          <div className="border-t border-gray-200 p-4">
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              total={total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
